import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Sparkles, RefreshCw, Save } from "lucide-react";
import { LangStatusIcon, StatusPill, shortLang, statusTitle, fmtDate } from "@/components/design/langStatus";

/** Iter 159 — Pestañas de idioma para título/descripción comercial de un Add-on. */
export function AddonI18nPanel({ ruleId, lang, onLang, masterSlot, inputCls, labelCls, refreshKey }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/addons/rules/${ruleId}/i18n`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { if (ruleId) load(); }, [ruleId, refreshKey]); // eslint-disable-line

  const d = data?.per_lang?.[lang];
  useEffect(() => {
    if (d) setForm({ title: d.fields.title || "", description: d.fields.description || "" });
  }, [lang, data]); // eslint-disable-line

  if (!data) return masterSlot;
  const langs = [{ code: data.master, name: "Español", is_master: true }, ...data.languages];
  const isMaster = lang === data.master;

  const translate = async () => {
    let force = false;
    if (d?.reviewed) {
      if (!window.confirm("Esta traducción fue modificada manualmente. ¿Quieres sustituirla por una nueva traducción automática?")) return;
      force = true;
    }
    setBusy(true);
    try {
      const r = await api.post(`/addons/rules/${ruleId}/i18n/${lang}/translate`, { force });
      if (r.data.result?.errors?.length) toast.error(r.data.result.errors.map((e) => e.error).join(" · "));
      else toast.success(`Traducción ${shortLang(lang)} actualizada`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const saveManual = async () => {
    setBusy(true);
    try {
      const fields = {};
      if (d.master_fields.title !== undefined) fields.title = form.title;
      if (d.master_fields.description !== undefined) fields.description = form.description;
      await api.put(`/addons/rules/${ruleId}/i18n/${lang}`, { fields });
      toast.success("Traducción guardada como revisada manualmente");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const dirty = d && (form.title !== (d.fields.title || "") || form.description !== (d.fields.description || ""));
  const hasMaster = d && Object.keys(d.master_fields || {}).length > 0;

  return (
    <div className="space-y-3" data-testid="addon-i18n-panel">
      <div className="inline-flex h-8 border border-[#111110]" data-testid="addon-lang-tabs">
        {langs.map((l) => {
          const st = l.is_master ? null : data.per_lang[l.code];
          return (
            <button key={l.code} type="button" onClick={() => onLang(l.code)} data-testid={`addon-lang-${l.code}`}
              title={l.is_master ? `${l.name} · idioma maestro` : `${l.name} · ${statusTitle(st)}`}
              className={`px-2.5 text-[11px] font-mono uppercase tracking-wider inline-flex items-center gap-1 border-l border-[#111110] first:border-l-0 ${lang === l.code ? "bg-[#111110] text-white" : "bg-white hover:bg-[#F7F7F4]"}`}>
              {shortLang(l.code)}{st && <LangStatusIcon st={st} size={10} />}
            </button>
          );
        })}
      </div>

      {isMaster ? masterSlot : !hasMaster ? (
        <div className="text-xs text-[#525252] border border-dashed border-[#B5B5B5] p-3" data-testid="addon-i18n-empty">
          Escribe primero el título/descripción en español (pestaña ES) y guarda el Add-on.
        </div>
      ) : (
        <div className="space-y-3 border border-[#E5E5E0] bg-[#FAFAF8] p-3">
          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px]">
            <StatusPill st={{ status: d.status, reviewed: d.reviewed }} testid="addon-i18n-status" />
            <span className="text-[#525252]">Actualizada: {fmtDate(d.updated_at)} · {d.reviewed ? "Revisada manualmente" : d.status === "missing" ? "—" : "Automática (OpenAI)"}</span>
          </div>
          {d.master_fields.title !== undefined && (
            <div>
              <label className={labelCls}>Título comercial · {shortLang(lang)}</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="addon-i18n-title" className={inputCls}
                placeholder={d.master_fields.title} />
            </div>
          )}
          {d.master_fields.description !== undefined && (
            <div>
              <label className={labelCls}>Descripción · {shortLang(lang)}</label>
              <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="addon-i18n-description" className={inputCls}
                placeholder={d.master_fields.description} />
            </div>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={translate} disabled={busy} data-testid="addon-i18n-translate" className="ds-btn">
              {busy ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {d.status === "missing" ? "Traducir con OpenAI" : d.status === "outdated" ? "Actualizar traducción" : "Regenerar con OpenAI"}
            </button>
            <button type="button" onClick={saveManual} disabled={busy || !dirty} data-testid="addon-i18n-save" className="ds-btn primary">
              <Save size={12} /> Guardar traducción
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
